<script setup lang="ts">
interface PostCard {
  slug: string
  title: string
  excerpt: string | null
  cover_image_url: string | null
  tags: string[]
  published_at: string | null
}

const route = useRoute()
const page = computed(() => Math.max(1, Number(route.query.page) || 1))
const LIMIT = 12

const { data } = await useAsyncData(
  () => `blog-${page.value}`,
  () => $fetch<{ posts: PostCard[], total: number }>('/api/blog', {
    query: { page: page.value, limit: LIMIT }
  }).catch(() => ({ posts: [], total: 0 })),
  { watch: [page] }
)

const posts = computed(() => data.value?.posts ?? [])
const total = computed(() => data.value?.total ?? 0)

const title = 'Blog: link-in-bio guides and comparisons | mychar.gg'
const description = 'Guides, comparisons, and tips on link-in-bio pages: customization, effects, music, analytics, and how to set up your page for TikTok, Instagram, Twitch, and more.'

useSeoMeta({
  title,
  titleTemplate: null,
  ogTitle: 'mychar.gg blog',
  description,
  ogDescription: description
})

useHead({
  link: [{ rel: 'alternate', type: 'application/rss+xml', title: 'mychar.gg blog', href: '/blog/feed.xml' }]
})

defineOgImageComponent('Saas')

useSchemaOrg([defineWebPage({ '@type': 'CollectionPage' })])

function formatDate(date?: string | null): string {
  if (!date) return ''
  return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}
</script>

<template>
  <div>
    <UPageHero
      title="Blog"
      description="Guides and comparisons on building a link in bio that stands out."
      :ui="{ container: 'py-16 sm:py-20' }"
    />

    <UContainer class="pb-24">
      <div
        v-if="posts.length > 0"
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        <NuxtLink
          v-for="post in posts"
          :key="post.slug"
          :to="`/blog/${post.slug}`"
          class="group flex flex-col overflow-hidden rounded-2xl border border-default bg-elevated/40 shadow-(--surface-shadow) transition-colors hover:border-accented"
        >
          <div
            v-if="post.cover_image_url"
            class="aspect-video overflow-hidden"
          >
            <NuxtImg
              :src="post.cover_image_url"
              :alt="post.title"
              class="size-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
            />
          </div>
          <div class="flex flex-1 flex-col p-5">
            <div
              v-if="post.tags.length"
              class="mb-2 flex flex-wrap gap-1.5"
            >
              <span
                v-for="tag in post.tags.slice(0, 2)"
                :key="tag"
                class="rounded-full bg-primary-500/10 px-2 py-0.5 text-[11px] font-medium text-primary-400"
              >
                {{ tag }}
              </span>
            </div>
            <h2 class="text-lg font-semibold text-highlighted group-hover:text-primary-400 transition-colors">
              {{ post.title }}
            </h2>
            <p
              v-if="post.excerpt"
              class="mt-2 line-clamp-3 text-sm text-muted"
            >
              {{ post.excerpt }}
            </p>
            <p class="mt-4 flex items-center gap-1.5 text-xs text-dimmed">
              <AppLogoMark class="size-3.5" />
              <span>mychar.gg<template v-if="post.published_at"> · {{ formatDate(post.published_at) }}</template></span>
            </p>
          </div>
        </NuxtLink>
      </div>

      <div
        v-else
        class="rounded-2xl border border-default p-12 text-center text-muted"
      >
        No posts yet. Check back soon.
      </div>

      <div
        v-if="total > LIMIT"
        class="mt-10 flex justify-center"
      >
        <UPagination
          :default-page="page"
          :items-per-page="LIMIT"
          :total="total"
          :to="(p: number) => ({ query: { page: p } })"
        />
      </div>
    </UContainer>
  </div>
</template>
